import { Component, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import {
  CONFIRM_ACTION_COPY,
  ConfirmBulkAction,
  ConfirmDialogData,
  ConfirmDialogToggleResult,
  getConfirmDialogIconClass,
  getConfirmDialogToneFromIcon
} from '@core/models/confirm-dialog.model';

type ConfirmDialogResult = boolean | ConfirmDialogToggleResult;

@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [MatDialogModule],
  templateUrl: './confirm-dialog.html',
  styleUrl: './confirm-dialog.scss'
})
export class ConfirmDialog {
  private readonly dialogRef = inject<MatDialogRef<ConfirmDialog, ConfirmDialogResult>>(MatDialogRef);
  private readonly data = inject<ConfirmDialogData>(MAT_DIALOG_DATA);

  readonly title = this.data.title;
  readonly message = this.data.message;
  readonly entityLabel = this.data.entityLabel?.trim() ?? '';
  readonly icon = this.data.icon ?? 'warning';
  readonly confirmText = this.data.confirmText ?? 'Confirm';
  readonly cancelText = this.data.cancelText ?? 'Cancel';
  readonly tone = this.data.confirmTone ?? getConfirmDialogToneFromIcon(this.icon);
  readonly toggle = this.data.toggle ?? null;
  readonly toggled = signal(this.data.toggle?.defaultValue ?? false);

  get iconClass(): string {
    return getConfirmDialogIconClass(this.icon);
  }

  confirm(): void {
    // With a toggle the dialog resolves to its state; otherwise to `true`.
    this.dialogRef.close(this.toggle ? { toggled: this.toggled() } : true);
  }

  close(): void {
    this.dialogRef.close(false);
  }

  /**
   * Opens this dialog.
   * @returns `true` when the user confirms, otherwise `false`.
   */
  static open(dialog: MatDialog, data: ConfirmDialogData): Promise<boolean> {
    return firstValueFrom(ConfirmDialog.openRef(dialog, data).afterClosed()).then((result) => result === true);
  }

  /**
   * Opens this dialog with `data.toggle`.
   * @returns the toggle state when the user confirms, otherwise `null`.
   */
  static openWithToggle(dialog: MatDialog, data: ConfirmDialogData): Promise<boolean | null> {
    return firstValueFrom(ConfirmDialog.openRef(dialog, data).afterClosed()).then((result) =>
      typeof result === 'object' && result !== null ? result.toggled : null
    );
  }

  /**
   * Opens this dialog using the standard delete/archive/restore copy for list pages:
   * title is the action name, message asks "Are you sure you want to {action} {entity}?" —
   * same "Are you sure...?" pattern as the Logout confirmation.
   * @param entityName singular noun, e.g. 'participant'. Pass `entityNamePlural` for irregular plurals.
   */
  static confirmAction(
    dialog: MatDialog,
    action: ConfirmBulkAction,
    entityName: string,
    isSingle: boolean,
    entityNamePlural = `${entityName}s`
  ): Promise<boolean> {
    const copy = CONFIRM_ACTION_COPY[action];
    const noun = isSingle ? `this ${entityName}` : `selected ${entityNamePlural}`;

    return ConfirmDialog.open(dialog, {
      title: copy.confirmText,
      message: `Are you sure you want to ${copy.confirmText.toLowerCase()} ${noun}?`,
      icon: copy.icon,
      confirmText: copy.confirmText,
      cancelText: 'Cancel',
      confirmTone: copy.confirmTone
    });
  }

  private static openRef(dialog: MatDialog, data: ConfirmDialogData): MatDialogRef<ConfirmDialog, ConfirmDialogResult> {
    return dialog.open<ConfirmDialog, ConfirmDialogData, ConfirmDialogResult>(ConfirmDialog, {
      data,
      disableClose: true,
      autoFocus: false,
      width: '420px',
      panelClass: 'app-confirm-dialog-panel'
    });
  }
}
